export interface Lesson {
  title: string;
  intro: string;
  explanation: string[];
  keyConcepts?: Array<{ term: string; definition: string }>;
  practicalExample: {
    title: string;
    description: string;
    steps?: string[];
  };
  commonError: {
    problem: string;
    solution: string;
  };
  directApplication: string[];
}

export const lessonsData: Lesson[] = [
  // Lección 1: Planos y Ejes de Movimiento
  {
    title: 'Planos Anatómicos y Ejes de Movimiento',
    intro: 'Todo movimiento humano ocurre en planos tridimensionales y alrededor de ejes específicos. Comprender esto es fundamental para analizar ejercicios y diseñar programas efectivos.',
    explanation: [
      'El cuerpo humano se mueve en tres planos anatómicos principales: sagital (divide el cuerpo en izquierda y derecha), frontal o coronal (divide en anterior y posterior), y transversal u horizontal (divide en superior e inferior).',
      'Cada plano tiene un eje de rotación perpendicular: el plano sagital rota sobre el eje frontal (flexión/extensión), el plano frontal sobre el eje sagital (abducción/aducción), y el plano transversal sobre el eje longitudinal (rotación).',
      'La mayoría de ejercicios de gimnasio ocurren principalmente en el plano sagital (sentadillas, press, peso muerto), pero los movimientos funcionales reales combinan múltiples planos simultáneamente.',
      'Entender los planos te permite identificar qué músculos trabajan en cada ejercicio, detectar compensaciones y diseñar programas equilibrados que no descuiden ningún patrón de movimiento.'
    ],
    keyConcepts: [
      {
        term: 'Plano Sagital',
        definition: 'Movimientos hacia adelante y atrás: flexión, extensión. Ejemplos: sentadilla, curl de bíceps, peso muerto.'
      },
      {
        term: 'Plano Frontal',
        definition: 'Movimientos laterales: abducción, aducción. Ejemplos: elevaciones laterales, desplazamientos laterales.'
      },
      {
        term: 'Plano Transversal',
        definition: 'Movimientos de rotación: rotación interna/externa. Ejemplos: giros de tronco, rotaciones de cadera.'
      },
      {
        term: 'Movimiento Multiplanar',
        definition: 'Combinación de planos en un solo movimiento. Más funcional y complejo. Ejemplo: lanzamiento, swing con kettlebell.'
      }
    ],
    practicalExample: {
      title: 'Análisis del Press de Banca',
      description: 'El press de banca es principalmente un movimiento en plano sagital (flexión/extensión de hombro y codo), pero incluye componentes del plano frontal (aducción horizontal del hombro) y requiere estabilización en el plano transversal.',
      steps: [
        'Fase excéntrica: extensión de hombro y flexión de codo en plano sagital',
        'Punto de máxima tensión: cuando el brazo de momento es mayor (barra a media altura)',
        'Fase concéntrica: flexión de hombro y extensión de codo, con aducción horizontal',
        'Estabilización: los músculos del core previenen rotación no deseada en plano transversal'
      ]
    },
    commonError: {
      problem: 'Entrenar solo en plano sagital (press, sentadillas, peso muerto) y descuidar completamente movimientos laterales y rotacionales, creando desequilibrios y mayor riesgo de lesión.',
      solution: 'Incluir al menos un ejercicio por sesión que trabaje plano frontal (elevaciones laterales, desplazamientos) y transversal (rotaciones con cable, woodchops), especialmente en deportes que requieren cambios de dirección.'
    },
    directApplication: [
      'Al diseñar una rutina, asegúrate de incluir ejercicios en los tres planos para desarrollo equilibrado',
      'Los atletas de deportes con cambios de dirección necesitan más énfasis en planos frontal y transversal',
      'Si un cliente tiene dolor en un movimiento, analiza en qué plano ocurre para identificar debilidades específicas',
      'Los ejercicios unilaterales (una pierna, un brazo) fuerzan mayor estabilización multiplanar'
    ]
  },

  // Lección 2: Tipos de Articulaciones
  {
    title: 'Tipos de Articulaciones y Función Mecánica',
    intro: 'Las articulaciones determinan qué movimientos son posibles y cuánta carga pueden soportar. Conocer su estructura te permite entrenar de forma segura y efectiva.',
    explanation: [
      'Las articulaciones se clasifican según su estructura y movilidad. Las más relevantes para el entrenamiento son las sinoviales (móviles), que incluyen bisagra, pivote, esférica, elipsoidal, silla de montar y plana.',
      'Las articulaciones tipo bisagra (codo, rodilla) permiten movimiento en un solo plano con gran estabilidad. Las esféricas (hombro, cadera) permiten movimiento en múltiples planos pero son menos estables.',
      'La estabilidad articular viene de tres fuentes: estructura ósea (congruencia), ligamentos (restricción pasiva) y músculos (estabilización activa). El entrenamiento puede mejorar solo la última.',
      'Cada articulación tiene un rango de movimiento (ROM) óptimo. Forzar más allá de este rango bajo carga aumenta el riesgo de lesión. El ROM funcional es más importante que el ROM máximo pasivo.'
    ],
    keyConcepts: [
      {
        term: 'Articulación Bisagra',
        definition: 'Movimiento en un plano (flexión/extensión). Muy estable. Ejemplos: codo, rodilla, tobillo.'
      },
      {
        term: 'Articulación Esférica',
        definition: 'Movimiento en múltiples planos. Menos estable, más móvil. Ejemplos: hombro, cadera.'
      },
      {
        term: 'Estabilidad vs Movilidad',
        definition: 'Trade-off inevitable: más movilidad = menos estabilidad inherente. El entrenamiento debe respetar esto.'
      },
      {
        term: 'ROM Funcional',
        definition: 'Rango de movimiento que puedes controlar bajo carga. Más importante que flexibilidad pasiva.'
      }
    ],
    practicalExample: {
      title: 'Comparación Hombro vs Cadera',
      description: 'Ambas son articulaciones esféricas, pero la cadera es mucho más estable debido a su estructura ósea profunda (acetábulo). El hombro sacrifica estabilidad por movilidad extrema.',
      steps: [
        'Cadera: cavidad profunda, ligamentos fuertes, soporta cargas muy altas (sentadilla, peso muerto)',
        'Hombro: cavidad superficial, depende de músculos para estabilidad (manguito rotador)',
        'Implicación: el hombro requiere más trabajo de estabilización y progresión más cuidadosa',
        'Ejercicios de hombro deben incluir trabajo específico de manguito rotador y escapular'
      ]
    },
    commonError: {
      problem: 'Forzar rango de movimiento extremo bajo carga pesada en articulaciones móviles (ejemplo: press tras nuca con barra, sentadilla profunda con movilidad limitada), comprometiendo la integridad articular.',
      solution: 'Trabaja primero la movilidad sin carga. Luego carga solo el rango que puedes controlar con técnica perfecta. El ROM bajo carga debe ganarse progresivamente, no forzarse.'
    },
    directApplication: [
      'Articulaciones móviles (hombro, cadera) necesitan más trabajo de estabilización y control motor',
      'Articulaciones estables (codo, rodilla) toleran mejor cargas altas en su rango natural',
      'Si un cliente tiene dolor articular, reduce el ROM bajo carga y trabaja estabilidad',
      'Los ejercicios compuestos deben respetar el ROM natural de la articulación más limitante de la cadena'
    ]
  },

  // Lección 3: Palancas Biomecánicas
  {
    title: 'Palancas Biomecánicas: Primer, Segundo y Tercer Grado',
    intro: 'El cuerpo humano funciona como un sistema de palancas. Entender esto explica por qué algunos ejercicios son más difíciles que otros y cómo optimizar la técnica.',
    explanation: [
      'Una palanca tiene tres componentes: fulcro (punto de apoyo), resistencia (carga) y fuerza (músculo). Según su posición relativa, se clasifican en tres tipos.',
      'Palanca de primer grado: fulcro entre fuerza y resistencia (balancín). Ejemplo: extensión de cuello, equilibrio en la columna. Permite equilibrio y precisión.',
      'Palanca de segundo grado: resistencia entre fulcro y fuerza (carretilla). Ejemplo: elevación de talones. Ventaja mecánica alta, genera mucha fuerza.',
      'Palanca de tercer grado: fuerza entre fulcro y resistencia (pinzas). Ejemplo: curl de bíceps, la mayoría de movimientos humanos. Desventaja mecánica pero permite velocidad y rango de movimiento amplio.',
      'La mayoría de movimientos humanos son palancas de tercer grado: sacrificamos eficiencia mecánica por velocidad y movilidad. Por eso los músculos deben generar fuerzas mucho mayores que la carga externa.'
    ],
    keyConcepts: [
      {
        term: 'Palanca de Primer Grado',
        definition: 'Fulcro en el centro. Equilibrio y precisión. Ejemplo: cabeza sobre columna cervical.'
      },
      {
        term: 'Palanca de Segundo Grado',
        definition: 'Resistencia en el centro. Ventaja mecánica. Ejemplo: elevación de talones (gastrocnemios).'
      },
      {
        term: 'Palanca de Tercer Grado',
        definition: 'Fuerza en el centro. Desventaja mecánica pero velocidad. Ejemplo: curl de bíceps, la mayoría de movimientos.'
      },
      {
        term: 'Ventaja Mecánica',
        definition: 'Relación entre brazo de fuerza y brazo de resistencia. Determina cuánta fuerza muscular se necesita.'
      }
    ],
    practicalExample: {
      title: 'Análisis del Curl de Bíceps',
      description: 'El curl de bíceps es una palanca de tercer grado perfecta. El codo es el fulcro, el bíceps se inserta cerca del codo (fuerza), y la mancuerna está en la mano (resistencia).',
      steps: [
        'Fulcro: articulación del codo',
        'Fuerza: inserción del bíceps en el radio (muy cerca del fulcro)',
        'Resistencia: mancuerna en la mano (lejos del fulcro)',
        'Resultado: el bíceps debe generar fuerza 7-8 veces mayor que el peso de la mancuerna',
        'Por eso 10kg en la mano requieren ~70-80kg de tensión en el tendón del bíceps'
      ]
    },
    commonError: {
      problem: 'No entender que la dificultad de un ejercicio cambia radicalmente según la posición de la carga respecto al fulcro. Ejemplo: mantener brazos extendidos con mancuernas (elevaciones frontales) vs brazos flexionados.',
      solution: 'Reconoce que alejar la carga del fulcro aumenta exponencialmente la dificultad. Usa esto estratégicamente: brazos extendidos para mayor intensidad, flexionados para facilitar o cuando la técnica se degrada.'
    },
    directApplication: [
      'Entender por qué los ejercicios con brazos extendidos (aperturas, elevaciones) requieren menos peso que los de brazos flexionados (press)',
      'Modificar la dificultad de ejercicios corporales cambiando la posición del cuerpo (flexiones: pies elevados vs rodillas)',
      'Explicar a clientes por qué no pueden usar el mismo peso en todos los ejercicios de un grupo muscular',
      'Diseñar progresiones: acortar palanca primero, luego aumentar carga'
    ]
  },

  // Lección 4: Torque y Brazo de Momento
  {
    title: 'Torque y Brazo de Momento Aplicado a Ejercicios',
    intro: 'El torque (momento de fuerza) determina la dificultad real de un ejercicio en cada punto del movimiento. Dominar este concepto te permite optimizar la técnica y entender las curvas de resistencia.',
    explanation: [
      'Torque = Fuerza × Distancia perpendicular. En entrenamiento, es el peso × brazo de momento (distancia horizontal entre la carga y la articulación).',
      'El brazo de momento cambia constantemente durante un ejercicio. Cuando es máximo, el ejercicio es más difícil, aunque el peso no cambie.',
      'En una sentadilla, el torque en la cadera es máximo cuando el torso está más horizontal (abajo). En un curl, es máximo cuando el antebrazo está horizontal (90 grados).',
      'Entender el torque explica por qué algunos ejercicios son más duros en ciertas posiciones y por qué la técnica (ángulos corporales) afecta tanto la dificultad.',
      'Las máquinas con levas intentan mantener el torque constante a lo largo del movimiento, mientras que el peso libre tiene torque variable.'
    ],
    keyConcepts: [
      {
        term: 'Torque (Momento de Fuerza)',
        definition: 'Fuerza de rotación sobre una articulación. Torque = Peso × Brazo de momento. Medido en Newton-metro.'
      },
      {
        term: 'Brazo de Momento',
        definition: 'Distancia horizontal (perpendicular) entre la línea de fuerza y el fulcro. Determina la dificultad real.'
      },
      {
        term: 'Punto de Máximo Torque',
        definition: 'Posición donde el brazo de momento es mayor. El punto más difícil del ejercicio (sticking point).'
      },
      {
        term: 'Línea de Gravedad',
        definition: 'Línea vertical que pasa por el centro de masa de la carga. Siempre apunta hacia abajo.'
      }
    ],
    practicalExample: {
      title: 'Análisis del Torque en Sentadilla',
      description: 'En la sentadilla, el torque en la cadera cambia drásticamente según la profundidad. Cuando estás arriba, el brazo de momento es pequeño. En el punto más bajo, el torso se inclina y el brazo de momento es máximo.',
      steps: [
        'Posición alta: torso vertical, brazo de momento pequeño, torque bajo, fácil',
        'Bajando: el torso se inclina hacia adelante, brazo de momento aumenta',
        'Punto bajo (paralelo): máxima inclinación del torso, máximo brazo de momento, máximo torque',
        'Por eso el punto más difícil es salir del agujero, no la posición alta',
        'Sentadilla frontal tiene menos torque en cadera porque el torso permanece más vertical'
      ]
    },
    commonError: {
      problem: 'Usar momentum o rebote en el punto de máximo torque (ejemplo: rebotar en el fondo de la sentadilla, usar impulso en el curl) para evitar la parte más difícil, eliminando el estímulo de entrenamiento justo donde más se necesita.',
      solution: 'Controla la velocidad especialmente en el punto de máximo torque. Haz una pausa de 1 segundo en la posición más difícil para asegurar que el músculo trabaja, no el momentum. Reduce el peso si es necesario.'
    },
    directApplication: [
      'Identifica el punto de máximo torque en cada ejercicio para saber dónde concentrar la atención técnica',
      'Modifica ángulos corporales para cambiar el brazo de momento y ajustar la dificultad (ejemplo: inclinación del torso en remo)',
      'Usa pausas en el punto de máximo torque para aumentar intensidad sin añadir peso',
      'Explica a clientes por qué un ejercicio es más duro en ciertas posiciones aunque el peso no cambie',
      'Compara ejercicios similares por su perfil de torque (sentadilla frontal vs trasera, press inclinado vs plano)'
    ]
  },

  // Lección 5: Curvas de Fuerza
  {
    title: 'Curvas de Fuerza y Resistencia Variable',
    intro: 'Los músculos no generan la misma fuerza en todo el rango de movimiento. Las curvas de fuerza explican por qué algunos ejercicios son más efectivos que otros y cómo optimizar el estímulo.',
    explanation: [
      'La capacidad de generar fuerza de un músculo varía según su longitud (relación longitud-tensión) y velocidad de contracción (relación fuerza-velocidad).',
      'Curva de fuerza ascendente: el ejercicio se vuelve más fácil a medida que avanzas (ejemplo: sentadilla, press). El punto más difícil está al inicio del movimiento concéntrico.',
      'Curva de fuerza descendente: el ejercicio se vuelve más difícil a medida que avanzas (ejemplo: curl de bíceps, elevaciones laterales). El punto más difícil está a mitad del recorrido.',
      'Curva de fuerza en campana: dificultad máxima en el medio del rango (ejemplo: press de banca). Combina cambios en brazo de momento y longitud muscular.',
      'El peso libre solo ofrece resistencia constante (gravedad), pero el torque varía. Las máquinas con levas o bandas pueden modificar la curva de resistencia para igualar mejor la curva de fuerza muscular.'
    ],
    keyConcepts: [
      {
        term: 'Relación Longitud-Tensión',
        definition: 'Los músculos generan más fuerza en longitudes intermedias. Muy estirados o muy acortados = menos fuerza.'
      },
      {
        term: 'Curva Ascendente',
        definition: 'Más fácil al final del movimiento. Ejemplo: sentadilla, press. Limitado por el punto más bajo.'
      },
      {
        term: 'Curva Descendente',
        definition: 'Más difícil a mitad del recorrido. Ejemplo: curl, elevaciones laterales. Tensión constante en el músculo.'
      },
      {
        term: 'Resistencia Acomodante',
        definition: 'Bandas o cadenas que aumentan la resistencia donde eres más fuerte, igualando la curva de fuerza.'
      }
    ],
    practicalExample: {
      title: 'Comparación Press de Banca vs Aperturas',
      description: 'El press de banca tiene curva en campana: difícil a media altura, más fácil arriba y abajo. Las aperturas tienen curva descendente: máxima dificultad cuando los brazos están horizontales.',
      steps: [
        'Press de banca: punto más difícil a 10-15cm del pecho (máximo brazo de momento)',
        'Arriba: brazos extendidos, brazo de momento pequeño, más fácil',
        'Aperturas: brazos extendidos horizontalmente = máximo brazo de momento = máxima dificultad',
        'Arriba: brazos juntos, brazo de momento casi cero, muy fácil',
        'Por eso las aperturas mantienen tensión constante en pectorales, mientras el press tiene un punto muerto'
      ]
    },
    commonError: {
      problem: 'Usar solo ejercicios con curva ascendente (press, sentadillas) y descuidar ejercicios con curva descendente (aperturas, elevaciones), perdiendo tensión constante en el músculo y desarrollo completo.',
      solution: 'Combina ejercicios con diferentes curvas de fuerza. Usa compuestos (curva ascendente) para carga pesada y volumen, y aislados (curva descendente) para tensión constante y trabajo metabólico.'
    },
    directApplication: [
      'Ejercicios compuestos (press, sentadilla): usa para fuerza máxima y volumen, acepta que hay un punto muerto',
      'Ejercicios de aislamiento (aperturas, elevaciones): usa para tensión constante y trabajo metabólico',
      'Añade bandas a ejercicios de curva ascendente para aumentar resistencia en el rango fácil',
      'Usa cadenas en sentadilla/press para que la carga aumente conforme te vuelves más fuerte en el movimiento',
      'Explica a clientes por qué necesitan ambos tipos de ejercicios para desarrollo completo'
    ]
  },

  // Lección 6: Biomecánica de Ejercicios Clave
  {
    title: 'Biomecánica Aplicada a Ejercicios Clave',
    intro: 'Análisis biomecánico profundo de los cinco ejercicios fundamentales: sentadilla, press de banca, peso muerto, dominadas y press militar. Comprende qué músculos trabajan, por qué, y cómo optimizar la técnica.',
    explanation: [
      'Cada ejercicio tiene una biomecánica única que determina qué músculos trabajan más, dónde está el punto de máxima dificultad, y cómo modificar la técnica para diferentes objetivos.',
      'SENTADILLA: Ejercicio de cadena posterior y cuádriceps. El torque en cadera y rodilla varía según la profundidad y la posición del torso. Sentadilla alta (barra alta, torso vertical) enfatiza cuádriceps. Sentadilla baja (barra baja, torso inclinado) enfatiza glúteos y cadena posterior.',
      'PRESS DE BANCA: Movimiento de empuje horizontal. Trabaja pectoral, deltoides anterior y tríceps. El punto de máximo torque está a media altura. El agarre ancho enfatiza pectoral, el agarre estrecho enfatiza tríceps. El arco lumbar reduce el rango de movimiento y aumenta la estabilidad.',
      'PESO MUERTO: Ejercicio de bisagra de cadera. Trabaja toda la cadena posterior: glúteos, isquiotibiales, erectores espinales, trapecios. El torque en la cadera es máximo cuando la barra está despegando del suelo. Convencional enfatiza más espalda baja, sumo enfatiza más cuádriceps y aductores.',
      'DOMINADAS: Ejercicio de tracción vertical. Trabaja dorsales, bíceps, romboides. El punto más difícil es a media altura. Agarre prono (pull-up) enfatiza dorsales, agarre supino (chin-up) enfatiza más bíceps. El rango completo requiere depresión escapular activa.',
      'PRESS MILITAR: Empuje vertical. Trabaja deltoides (especialmente anterior y medio), tríceps, trapecio superior. Requiere estabilidad del core para evitar hiperextensión lumbar. El punto de máximo torque está cuando la barra pasa la cabeza.'
    ],
    keyConcepts: [
      {
        term: 'Sentadilla: Profundidad',
        definition: 'Paralelo = máximo trabajo de glúteos. Más profundo = más cuádriceps pero requiere movilidad de tobillo y cadera.'
      },
      {
        term: 'Press Banca: Trayectoria',
        definition: 'La barra debe bajar hacia el esternón bajo, no vertical. Trayectoria diagonal optimiza biomecánica del hombro.'
      },
      {
        term: 'Peso Muerto: Bisagra de Cadera',
        definition: 'Movimiento dominado por extensión de cadera, no de espalda. La espalda se mantiene neutra, el movimiento viene de glúteos.'
      },
      {
        term: 'Dominadas: Depresión Escapular',
        definition: 'Bajar los hombros activamente antes de tirar. Activa dorsales y protege el hombro.'
      }
    ],
    practicalExample: {
      title: 'Optimización de la Sentadilla según Objetivo',
      description: 'La misma sentadilla puede modificarse para enfatizar diferentes músculos cambiando la posición de la barra, el ancho de stance y la profundidad.',
      steps: [
        'Para cuádriceps: barra alta, stance estrecho, torso vertical, sentadilla profunda, talones elevados',
        'Para glúteos y cadena posterior: barra baja, stance ancho, mayor inclinación del torso, paralelo',
        'Para fuerza máxima: barra baja, stance medio-ancho, profundidad a paralelo, zapatos de halterofilia',
        'Para seguridad con movilidad limitada: barra alta, stance ancho, profundidad controlada, trabajo de movilidad paralelo',
        'Todos son válidos, depende del objetivo y las características individuales'
      ]
    },
    commonError: {
      problem: 'Aplicar una técnica única y dogmática a todos los ejercicios sin considerar las diferencias anatómicas individuales (longitud de fémur, movilidad de tobillo, proporción torso-piernas) que hacen que la técnica óptima varíe entre personas.',
      solution: 'La técnica debe individualizarse. Usa los principios biomecánicos (mantener la barra sobre el centro de masa, respetar el ROM articular) pero ajusta los detalles (ancho de stance, profundidad, ángulo del torso) según la anatomía y objetivo de cada persona.'
    },
    directApplication: [
      'Evalúa la anatomía del cliente antes de prescribir técnica: longitud de extremidades, movilidad articular, historial de lesiones',
      'Modifica ejercicios según el objetivo: hipertrofia (mayor ROM, tensión constante) vs fuerza (técnica que permite más carga)',
      'Si un ejercicio causa dolor, analiza la biomecánica: puede ser un problema de técnica, movilidad o simplemente incompatibilidad anatómica',
      'Usa variantes de ejercicios para trabajar el mismo patrón con diferente énfasis muscular',
      'Enseña a los clientes a sentir qué músculos trabajan en cada variante para que entiendan por qué haces ciertos ajustes'
    ]
  }
];
